import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import type {
    BulkApproveAccrualReviewBody,
    BulkApproveAccrualReviewResponse,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import type { AccountOverrideEntryRequest } from "@common/schemas/domains/accounting/account-override.schema";
import { Ledger } from "@common/contracts/ledger.contract";
import { pluralize } from "@common/helpers/string";
import { useScope } from "@state/scopeContext.lib";
import { useBulkActions } from "@components/transactions/TransactionsBulkActions/useBulkActions";
import { scheduleConflictSkippedMessage } from "./scheduleConflictSkippedMessage";
import { PaginatedTransactionsDataContext } from "@components/transactions/PaginatedTransactionsTable.context";
import { bulkApproveAccrualReview } from "@api/accrual-review.api";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    classifyTransactions,
    buildBulkRequestBody,
    buildBulkRequestBodyForFilters,
} from "./accrualReviewBulkApprove";
import { DEFAULT_ACCRUAL_REVIEW_LEDGER_KEY } from "./accrualReview.constants";
import { applyBulkApproveToAccrualReviewCache } from "./accrualReviewCache";
import { useAccrualReviewContext } from "./useAccrualReviewContext";

interface BulkAcceptClassification {
    requestBody: BulkApproveAccrualReviewBody;
    acceptableCount: number;
    reviewRequiredCount: number;
    alreadyAcceptedCount: number;
}

/**
 * Page size for the filter-driven drain. Matches the backend's natural page
 * size in `loadTransactionsByFiltersPage` and stays well under the schema's
 * `limit` cap (200). Small enough that each round-trip is responsive; large
 * enough that draining a big queue takes few requests.
 */
const FILTER_DRAIN_PAGE_SIZE = 100;

/**
 * Safety cap on the number of round-trips a single "Confirm all" run will
 * make — avoids a runaway loop if the backend repeatedly returns non-empty
 * pages where no row can be advanced (broken state).
 * 100 iterations × 100 rows = 10,000 transactions per click, which is far
 * beyond any realistic accrual-review task bucket.
 */
const FILTER_DRAIN_MAX_ITERATIONS = 100;

/**
 * accountOverrides cap enforced by `bulkApproveAccrualReviewBodySchema`.
 * Truncate silently on the client so we never produce an invalid request
 * when the user has set more overrides than this across pages.
 */
const ACCOUNT_OVERRIDES_MAX = 500;

function truncateOverrides(
    overrides: ReadonlyMap<number, ReadonlyMap<string, string>>,
): Map<number, ReadonlyMap<string, string>> {
    if (overrides.size <= ACCOUNT_OVERRIDES_MAX) {
        return new Map(overrides);
    }
    const truncated = new Map<number, ReadonlyMap<string, string>>();
    let i = 0;
    for (const [txId, accountsByLedger] of overrides) {
        if (i >= ACCOUNT_OVERRIDES_MAX) {
            break;
        }
        truncated.set(txId, accountsByLedger);
        i++;
    }
    return truncated;
}

interface DrainTotals {
    approvedCount: number;
    failedCount: number;
    skippedReviewCount: number;
    skippedScheduleConflictCount: number;
    skippedAlreadyAcceptedCount: number;
}

function buildAccrualLedgerAccountOverrides({
    ledgers,
    getAccountId,
}: {
    ledgers: Ledger[];
    getAccountId: (ledgerName: string) => string | null | undefined;
}): AccountOverrideEntryRequest[] {
    return ledgers
        .filter((ledger) => ledger.basis === AccountingLedgerBasis.accruals)
        .flatMap((ledger) => {
            const accountId = getAccountId(ledger.name);
            return accountId
                ? [{ accountingLedgerId: ledger.id, accountId }]
                : [];
        });
}

export function useAccrualBulkAccept() {
    const bulkActions = useBulkActions();
    const { workspaceId } = useScope();
    const { transactions, filters, transactionCount } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { isMatchDismissed, getProposedAccountId, overrides } =
        useAccrualReviewContext();
    const entityIds = useMemo(
        () => [
            ...new Set(
                (transactions ?? []).map(
                    (transaction) => transaction.entity.id,
                ),
            ),
        ],
        [transactions],
    );
    const { data: ledgersByEntityId } = useLedgersByEntityIds(entityIds);

    const { toast, error: toastError } = useToaster();
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [pendingClassification, setPendingClassification] =
        useState<BulkAcceptClassification | null>(null);
    const [isBulkAccepting, setIsBulkAccepting] = useState(false);
    const isBulkAcceptingRef = useRef(false);

    const transactionsById = useMemo(() => {
        const map = new Map<number, TransactionListItem>();
        for (const tx of transactions ?? []) {
            map.set(tx.id, tx);
        }
        return map;
    }, [transactions]);

    const getSelectedTransactions = useCallback((): TransactionListItem[] => {
        if (!transactions) {
            return [];
        }
        if (bulkActions.hasSelectedAll) {
            return transactions;
        }
        const selectedIds = new Set(
            Object.keys(bulkActions.selected).map(Number),
        );
        return transactions.filter((t) => selectedIds.has(t.id));
    }, [transactions, bulkActions.hasSelectedAll, bulkActions.selected]);

    const showDrainSummaryToast = useCallback(
        (totals: DrainTotals) => {
            if (totals.approvedCount > 0) {
                toast(
                    `${totals.approvedCount} ${pluralize("transaction", totals.approvedCount)} confirmed`,
                );
            }
            const skipped =
                totals.skippedReviewCount + totals.skippedAlreadyAcceptedCount;
            if (skipped > 0) {
                toastError(
                    `${skipped} ${pluralize("transaction", skipped)} skipped (require review or already confirmed)`,
                );
            }
            if (totals.skippedScheduleConflictCount > 0) {
                toastError(
                    scheduleConflictSkippedMessage(
                        totals.skippedScheduleConflictCount,
                    ),
                );
            }
            if (totals.failedCount > 0) {
                toastError(
                    `${totals.failedCount} ${pluralize("transaction", totals.failedCount)} couldn't be confirmed`,
                );
            }
            if (
                totals.approvedCount === 0 &&
                skipped === 0 &&
                totals.skippedScheduleConflictCount === 0 &&
                totals.failedCount === 0
            ) {
                toast("No transactions to confirm");
            }
        },
        [toast, toastError],
    );

    const executeBulkApprove = useCallback(
        async (body: BulkApproveAccrualReviewBody) => {
            if (!workspaceId || isBulkAcceptingRef.current) {
                return;
            }

            isBulkAcceptingRef.current = true;
            setIsBulkAccepting(true);
            try {
                const response = await bulkApproveAccrualReview({
                    workspaceId,
                    body,
                });

                applyBulkApproveToAccrualReviewCache({
                    updatedTransactions: response.updatedTransactions,
                    createdSplits: response.createdSplits,
                });

                if (response.approvedCount > 0) {
                    toast(
                        `${response.approvedCount} ${pluralize("transaction", response.approvedCount)} accepted`,
                    );
                }
                if (response.skippedScheduleConflictCount > 0) {
                    toastError(
                        scheduleConflictSkippedMessage(
                            response.skippedScheduleConflictCount,
                        ),
                    );
                }
                if (response.failedCount > 0) {
                    toastError(
                        `${response.failedCount} ${pluralize("transaction", response.failedCount)} couldn't be accepted`,
                    );
                }

                if (response.failedCount === 0) {
                    bulkActions.clearSelection();
                }
            } catch {
                toastError("Failed to accept transactions");
            } finally {
                isBulkAcceptingRef.current = false;
                setIsBulkAccepting(false);
            }
        },
        [workspaceId, bulkActions, toastError, toast],
    );

    /**
     * Filter-driven "Confirm all" / "Confirm filtered": repeatedly POST the
     * current filters to the bulk-approve endpoint, letting the server load
     * and process one page at a time. Every approved row drops out of the
     * `isNotAccrualReviewed: true` filter, so the queue naturally drains
     * without ever sending `transactionIds`. Handles pages of pure skipped
     * rows by advancing a page cursor past them; resets to page 0 whenever
     * any row was approved (the approvals shifted the queue).
     */
    const executeBulkApproveByFilters = useCallback(async (): Promise<void> => {
        if (!workspaceId || isBulkAcceptingRef.current) {
            return;
        }

        isBulkAcceptingRef.current = true;
        setIsBulkAccepting(true);

        const getAccountOverrides = (
            transactionId: number,
            accountsByLedger: ReadonlyMap<string, string>,
        ): string | AccountOverrideEntryRequest[] => {
            const defaultAccountId = accountsByLedger.get(
                DEFAULT_ACCRUAL_REVIEW_LEDGER_KEY,
            );
            const shouldUseLegacyPrimaryOverride =
                accountsByLedger.size === 1 && !!defaultAccountId;
            const transaction = transactionsById.get(transactionId);
            if (!transaction) {
                return shouldUseLegacyPrimaryOverride ? defaultAccountId : [];
            }
            const entries = buildAccrualLedgerAccountOverrides({
                ledgers: ledgersByEntityId?.[transaction.entity.id] ?? [],
                getAccountId: (ledgerName) =>
                    accountsByLedger.get(ledgerName) ?? defaultAccountId,
            });
            if (entries.length > 0) {
                return entries;
            }
            if (shouldUseLegacyPrimaryOverride) {
                return defaultAccountId;
            }
            return [];
        };

        const drain = async () => {
            const totals: DrainTotals = {
                approvedCount: 0,
                failedCount: 0,
                skippedReviewCount: 0,
                skippedScheduleConflictCount: 0,
                skippedAlreadyAcceptedCount: 0,
            };
            let pageCursor = 0;
            const overridesSnapshot = truncateOverrides(overrides);

            for (let iter = 0; iter < FILTER_DRAIN_MAX_ITERATIONS; iter++) {
                const body = buildBulkRequestBodyForFilters({
                    filters,
                    limit: FILTER_DRAIN_PAGE_SIZE,
                    overrides: overridesSnapshot,
                    getAccountOverrides,
                });
                body.page = pageCursor;

                let response: BulkApproveAccrualReviewResponse;
                try {
                    response = await bulkApproveAccrualReview({
                        workspaceId,
                        body,
                    });
                } catch {
                    toastError("Failed to confirm transactions");
                    break;
                }

                const processed =
                    response.approvedCount +
                    response.failedCount +
                    response.skippedReviewCount +
                    response.skippedScheduleConflictCount +
                    response.skippedAlreadyAcceptedCount;

                if (processed === 0) {
                    // Empty page — the filter is drained.
                    break;
                }

                totals.approvedCount += response.approvedCount;
                totals.failedCount += response.failedCount;
                totals.skippedReviewCount += response.skippedReviewCount;
                totals.skippedScheduleConflictCount +=
                    response.skippedScheduleConflictCount;
                totals.skippedAlreadyAcceptedCount +=
                    response.skippedAlreadyAcceptedCount;

                applyBulkApproveToAccrualReviewCache({
                    updatedTransactions: response.updatedTransactions,
                    createdSplits: response.createdSplits,
                });

                if (response.approvedCount > 0) {
                    // Approved rows dropped out of the filter — restart at
                    // page 0 so the next slice picks up fresh rows from the
                    // top of the remaining set.
                    pageCursor = 0;
                } else {
                    // No approvals this round — the page was entirely
                    // skipped/failed rows that will keep reappearing at
                    // page 0. Step forward past them.
                    pageCursor += 1;
                }
            }

            showDrainSummaryToast(totals);

            if (totals.failedCount === 0 && totals.approvedCount > 0) {
                bulkActions.clearSelection();
            }
        };
        await drain().finally(() => {
            isBulkAcceptingRef.current = false;
            setIsBulkAccepting(false);
        });
    }, [
        workspaceId,
        overrides,
        filters,
        showDrainSummaryToast,
        bulkActions,
        toastError,
        transactionsById,
        ledgersByEntityId,
    ]);

    const confirmTransactions = useCallback(
        (target: TransactionListItem[], scope: "selected" | "all") => {
            if (target.length === 0) {
                return;
            }

            const counts = classifyTransactions({
                transactions: target,
                getSuggestions: (tx) =>
                    isMatchDismissed(tx.id)
                        ? []
                        : (tx.accrualMatchSuggestions ?? []),
            });

            if (counts.acceptableTransactionIds.length === 0) {
                if (counts.reviewRequiredCount > 0) {
                    toastError(
                        scope === "selected"
                            ? "All selected transactions require review and can't be accepted in bulk."
                            : "All transactions require review and can't be accepted in bulk.",
                    );
                } else if (counts.alreadyAcceptedCount > 0) {
                    toastError(
                        scope === "selected"
                            ? "All selected transactions have already been accepted."
                            : "All transactions have already been accepted.",
                    );
                }
                return;
            }

            const requestBody = buildBulkRequestBody({
                transactionIds: counts.acceptableTransactionIds,
                transactionsById,
                getProposedAccountId,
                getProposedAccountOverrides: (transaction) =>
                    buildAccrualLedgerAccountOverrides({
                        ledgers:
                            ledgersByEntityId?.[transaction.entity.id] ?? [],
                        getAccountId: (ledgerName) =>
                            getProposedAccountId(transaction, ledgerName),
                    }),
            });

            const hasSkipped =
                counts.reviewRequiredCount > 0 ||
                counts.alreadyAcceptedCount > 0;
            if (hasSkipped) {
                setPendingClassification({
                    requestBody,
                    acceptableCount: counts.acceptableTransactionIds.length,
                    reviewRequiredCount: counts.reviewRequiredCount,
                    alreadyAcceptedCount: counts.alreadyAcceptedCount,
                });
                setIsConfirmModalVisible(true);
            } else {
                void executeBulkApprove(requestBody);
            }
        },
        [
            isMatchDismissed,
            transactionsById,
            getProposedAccountId,
            ledgersByEntityId,
            executeBulkApprove,
            toastError,
        ],
    );

    const handleBulkAccept = useCallback(() => {
        if (bulkActions.hasSelectedAll) {
            return;
        }
        confirmTransactions(getSelectedTransactions(), "selected");
    }, [
        bulkActions.hasSelectedAll,
        getSelectedTransactions,
        confirmTransactions,
    ]);

    /**
     * "Confirm all" / "Confirm filtered" — drains all transactions matching
     * the currently applied filters, not just the current page. Bypasses the
     * pre-flight confirmation modal because we do not know skip counts until
     * the server classifies each page; the summary toast surfaces the final
     * breakdown instead.
     */
    const handleConfirmAll = useCallback(() => {
        void executeBulkApproveByFilters();
    }, [executeBulkApproveByFilters]);

    const handleConfirm = useCallback(async () => {
        if (!pendingClassification) {
            return;
        }
        setIsConfirmModalVisible(false);
        await executeBulkApprove(pendingClassification.requestBody);
        setPendingClassification(null);
    }, [pendingClassification, executeBulkApprove]);

    const handleCancelConfirm = useCallback(() => {
        setIsConfirmModalVisible(false);
        setPendingClassification(null);
    }, []);

    const isBulkAcceptDisabled = isBulkAccepting || bulkActions.hasSelectedAll;
    const isConfirmAllDisabled = isBulkAccepting || transactionCount === 0;

    // Message shown under the confirmation modal title when the bulk accept
    // skips some rows (review-required or already-accepted). Computed here
    // so every consumer renders identical copy.
    const confirmModalMessage = useMemo(() => {
        if (!pendingClassification) {
            return "";
        }
        const { reviewRequiredCount, alreadyAcceptedCount } =
            pendingClassification;
        const lines: string[] = [];
        if (reviewRequiredCount > 0) {
            lines.push(
                `${reviewRequiredCount} ${pluralize("transaction", reviewRequiredCount)} require review and will not be accepted into the accrual ledger.`,
            );
        }
        if (alreadyAcceptedCount > 0) {
            lines.push(
                `${alreadyAcceptedCount} ${pluralize("transaction", alreadyAcceptedCount)} already accepted`,
            );
        }
        return lines.join("\n");
    }, [pendingClassification]);

    return {
        isBulkAcceptDisabled,
        isConfirmAllDisabled,
        isConfirmModalVisible,
        pendingClassification,
        confirmModalMessage,
        handleBulkAccept,
        handleConfirmAll,
        handleConfirm,
        handleCancelConfirm,
    };
}
